import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { GlobalLoader } from '@/components/ui/global-loader';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, hasMenuPermission, getFirstAllowedMenuPath, isInvitedUser, isMenuAllowedByPlan } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <GlobalLoader isLoading={true} />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const currentPath = location.pathname;

  // Standalone routes that don't require a DB menu entry
  const standaloneRoutes = ['/api-documentation', '/help_support/api-docs'];
  const isStandaloneRoute = standaloneRoutes.some(r => currentPath.toLowerCase().startsWith(r));
  if (isStandaloneRoute) {
    return <>{children}</>;
  }

  // Check if menu is allowed by pricing plan (applies to ALL users including tenant-admin)
  const isPlanAllowed = isMenuAllowedByPlan(currentPath);
  if (!isPlanAllowed) {
    const firstAllowedPath = getFirstAllowedMenuPath();
    // Never redirect an authenticated user back to /login — that creates a confusing loop
    if (firstAllowedPath === '/login' || firstAllowedPath === currentPath) {
      return <>{children}</>;
    }
    console.log(`[ProtectedRoute] Menu "${currentPath}" not allowed by plan, redirecting to "${firstAllowedPath}"`);
    return <Navigate to={firstAllowedPath} replace />;
  }

  // For invited users, also check role-based permissions
  if (isInvitedUser()) {
    const hasPermission = hasMenuPermission(currentPath);

    if (!hasPermission) {
      const firstAllowedPath = getFirstAllowedMenuPath();
      // Never redirect an authenticated user back to /login
      if (firstAllowedPath === '/login' || firstAllowedPath === currentPath) {
        return <>{children}</>;
      }
      console.log(`[ProtectedRoute] User doesn't have permission for "${currentPath}", redirecting to "${firstAllowedPath}"`);
      return <Navigate to={firstAllowedPath} replace />;
    }
  }

  return <>{children}</>;
}
